import {
  getTradeChartStateBadgeModel,
  type TradeChartResolutionResult,
} from '../../lib/chart/tradeChartResolution';

const BADGE_CLASSES: Record<string, string> = {
  live: 'bg-zinc-900 text-emerald-200 border-emerald-800/60',
  cached: 'bg-zinc-900 text-slate-200 border-slate-700/70',
  fresh: 'bg-sky-950/40 text-sky-200 border-sky-700/50',
  fallback: 'bg-amber-950/55 text-amber-100 border-amber-700/60',
  unavailable: 'bg-rose-950/55 text-rose-100 border-rose-700/60',
};

const DOT_CLASSES: Record<string, string> = {
  live: 'bg-emerald-400',
  cached: 'bg-slate-400',
  fresh: 'bg-sky-400',
  fallback: 'bg-amber-300',
  unavailable: 'bg-rose-300',
};

export default function TradeChartStateBadge({
  meta,
  resolution,
  className = '',
}: {
  meta?: Record<string, unknown> | null;
  resolution?: Partial<TradeChartResolutionResult> | null;
  className?: string;
}) {
  const badge = getTradeChartStateBadgeModel({ meta, resolution });
  if (!badge) return null;
  const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const LIGHT_BADGE_CLASSES: Record<string, string> = {
    live: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    cached: 'bg-slate-50 text-slate-800 border-slate-200',
    fresh: 'bg-sky-50 text-sky-800 border-sky-200',
    fallback: 'bg-amber-100 text-amber-800 border-amber-200',
    unavailable: 'bg-rose-100 text-rose-800 border-rose-200',
  };

  const LIGHT_DOT_CLASSES: Record<string, string> = {
    live: 'bg-emerald-600',
    cached: 'bg-slate-600',
    fresh: 'bg-sky-600',
    fallback: 'bg-amber-500',
    unavailable: 'bg-rose-500',
  };

  const badgeClass = dark ? BADGE_CLASSES[badge.kind] : LIGHT_BADGE_CLASSES[badge.kind] ?? 'bg-zinc-100 text-zinc-900 border-zinc-200';
  const dotClass = dark ? DOT_CLASSES[badge.kind] : LIGHT_DOT_CLASSES[badge.kind] ?? 'bg-zinc-700';

  return (
    <div
      data-testid="trade-chart-state-badge"
      data-kind={badge.kind}
      aria-label={`Chart state: ${badge.label}`}
      title={badge.title}
      className={`mt-1 inline-flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${badgeClass} ${className}`.trim()}
    >
      <span
        data-testid="trade-chart-state-badge-dot"
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${dotClass}`}
      />
      <span className="leading-none font-medium">{badge.label}</span>
    </div>
  );
}
